import React, { memo, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import Tabs, { TabPane } from 'rc-tabs';
import { useDispatch, useSelector } from 'react-redux';
import { Download } from '@saleshandy/icons';
import { CallLog } from '../../../../types/call-logs';
import AudioPlayer from '../../../audio-player/audio-player';
import { getSignedUrlForCallRecordingRequest } from '../../../../extra-actions';
import { RootState } from '../../../../../../store/root-reducer';
import CopyButton from '../../../../../../shared/components/copy-button';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../shared/design-system/components/overlay';
import './call-logs-outbound-detail-sections.scss';
import { AiGradientStarIcon } from '../../../../../../shared/svg/ai-gradient-star-icon';
import {
  accessibleOnClick,
  getFormatDate,
} from '../../../../../../shared/utils';
import Divider from '../../../../../settings/components/my-profile/components/change-email-modal/change-email-modal-flow/sub-components/divider';
import { TranscriptItem } from '../../../../types';
import {
  formatTranscriptionItemsAsPlainText,
  formatTime,
  parseCallTranscriptionToItems,
} from '../../utils/helpers';
import { Dot } from '../../../../../../shared/svg/dot';
import { getInitials } from '../../../../../../shared/components/dialer/utils/helper';

type OutboundInsightsTab = 'ai-summary' | 'recording';

/** API may return JSON transcript segments or plain text (e.g. voicemail greeting). */

export interface CallLogsOutboundDetailSectionsProps {
  row: CallLog;
  callLogsProspectProfileSection?: any;
  userTimeZone?: string;
  callLogsProspectDrawerListing?: boolean;
}

function getAiSummaryFromSources(
  row: CallLog,
  profile?: any,
): { text: string | null; pending: boolean } {
  const text =
    row.aiSummary ??
    profile?.aiSummary ??
    profile?.dialerPhoneNumberCallDetail?.aiSummary ??
    null;
  const pending =
    row.isAiSummaryInProgress ??
    profile?.isAiSummaryInProgress ??
    profile?.dialerPhoneNumberCallDetail?.isAiSummaryInProgress ??
    false;
  return {
    text: typeof text === 'string' && text.trim().length > 0 ? text : null,
    pending: Boolean(pending),
  };
}

const CallLogsAiSummaryMarkdown: React.FC<{
  text: string;
  className?: string;
}> = ({ text, className }) => (
  <div className={classNames('bs-call-logs-ai-summary-markdown', className)}>
    <ReactMarkdown>{text}</ReactMarkdown>
  </div>
);

const CallLogsOutboundDetailSections: React.FC<CallLogsOutboundDetailSectionsProps> = memo(
  ({
    row,
    callLogsProspectProfileSection,
    userTimeZone,
    callLogsProspectDrawerListing,
  }) => {
    const dispatch = useDispatch();

    const signedUrl = useSelector(
      (state: RootState) =>
        state?.dialer?.getSignedUrlForCallRecordingResponses?.[row?.callId]
          ?.signedUrl ?? null,
    );
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const [
      recordingDownloadUrlFetchPending,
      setRecordingDownloadUrlFetchPending,
    ] = useState(false);
    const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
    const [activeTab, setActiveTab] = useState<OutboundInsightsTab>(
      'ai-summary',
    );

    useEffect(() => {
      setActiveTab('ai-summary');
    }, [row.id, row.callId]);

    useEffect(() => {
      setRecordingDownloadUrlFetchPending(false);
    }, [row.id, row.callId]);

    const handlePlayWithoutSrc = useCallback(async () => {
      // Must match `row.callId`: Redux stores the response under `getSignedUrlForCallRecordingResponses[callId]`
      // where `callId` is this thunk argument (see dialer-slice fulfilled handler).
      const callIdNumber = Number(row.callId);
      if (!Number.isFinite(callIdNumber)) return;

      setShouldAutoPlay(true);
      setIsLoadingAudio(true);
      try {
        const action: any = await dispatch(
          getSignedUrlForCallRecordingRequest(callIdNumber) as any,
        );
        const payload = action?.payload?.payload ?? action?.payload;
        if (!payload?.signedUrl) {
          setIsLoadingAudio(false);
        }
      } catch {
        setIsLoadingAudio(false);
      }
    }, [dispatch, row.callId]);

    const handleDownloadWithoutSrc = useCallback(async () => {
      const callIdNumber = Number(row.callId);
      if (!Number.isFinite(callIdNumber)) return;

      setRecordingDownloadUrlFetchPending(true);
      try {
        const action: any = await dispatch(
          getSignedUrlForCallRecordingRequest(callIdNumber) as any,
        );
        const payload = action?.payload?.payload ?? action?.payload;
        if (!payload?.signedUrl) {
          return;
        }
      } catch {
        // Request failed; pending cleared in `finally`.
      } finally {
        setRecordingDownloadUrlFetchPending(false);
      }
    }, [dispatch, row.callId]);

    const audioSrc = signedUrl || row?.recordingUrl || null;

    const handleAutoPlayCompleted = useCallback(() => {
      setShouldAutoPlay(false);
      setIsLoadingAudio(false);
    }, []);

    const { isTranscriptionInProgress } = row;
    const transcription =
      callLogsProspectProfileSection?.dialerPhoneNumberCallDetail
        ?.transcription ??
      row?.transcription ??
      callLogsProspectProfileSection?.transcription;
    const transcriptionPending = isTranscriptionInProgress === true;
    const formattedTranscription = parseCallTranscriptionToItems(
      transcription ??
        callLogsProspectProfileSection?.dialerPhoneNumberCallDetail
          ?.transcription,
    );
    const items = parseCallTranscriptionToItems(transcription);
    const text = formatTranscriptionItemsAsPlainText(items);

    const handleDownloadFormattedTranscription = useCallback(() => {
      if (!text || typeof document === 'undefined') return;
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const safeId = String(row?.callId ?? row?.id ?? 'call').replace(
        /[^a-zA-Z0-9-_]/g,
        '',
      );
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `call-transcription-${safeId}.txt`;
      anchor.click();
      URL.revokeObjectURL(url);
    }, [transcription, row?.callId, row?.id]);

    const {
      text: aiSummaryText,
      pending: aiSummaryPending,
    } = getAiSummaryFromSources(row, callLogsProspectProfileSection);

    const hasRecordingAsset =
      row.isRecordingPresent === true ||
      Boolean(row.recordingUrl) ||
      Boolean(audioSrc);

    const hasTranscriptionBody =
      typeof transcription === 'string' && transcription.trim().length > 0;

    const recordingDownloadSafeId = String(
      row?.callId ?? row?.id ?? 'call',
    ).replace(/[^a-zA-Z0-9-_]/g, '');

    const recordingOrTranscriptionAvailable =
      hasRecordingAsset || hasTranscriptionBody || transcriptionPending;
    return (
      <div className="bs-call-logs-outbound-sections">
        {!callLogsProspectDrawerListing ? (
          <Tabs
            activeKey={activeTab}
            prefixCls="bs-tabs"
            className={`bs-tabs-top bs-tabs-small bs-call-logs-outbound-sections__tabs ${
              activeTab === 'recording' ? 'bs-call-logs-recording' : ''
            }`}
            destroyInactiveTabPane={false}
            onChange={(key) => setActiveTab(key as OutboundInsightsTab)}
            renderTabBar={(props, DefaultTabBar) => (
              <DefaultTabBar
                {...props}
                className={classNames(
                  props.className,
                  'bs-call-logs-outbound-sections__tablist',
                )}
              />
            )}
          >
            <TabPane
              tab={
                <span
                  className={classNames('bs-call-logs-outbound-sections__tab', {
                    'bs-call-logs-outbound-sections__tab--active':
                      activeTab === 'ai-summary',
                  })}
                >
                  <AiGradientStarIcon
                    width={16}
                    height={16}
                    className="bs-call-logs-outbound-sections__tab-icon"
                    activeType={activeTab === 'ai-summary'}
                  />
                  AI summary
                </span>
              }
              key="ai-summary"
            >
              <div
                className={`bs-call-logs-outbound-sections__tab-panel ${
                  activeTab === 'ai-summary' ? 'active-tab-ai-summary' : ''
                }`}
              >
                {!aiSummaryText && (
                  <div className="bs-call-logs-outbound-sections__panel bs-call-logs-outbound-sections__panel--filled bs-call-logs-outbound-sections__panel--no-ai-summary">
                    <p className="bs-call-logs-outbound-sections__no-ai-summary-title">
                      No conversation detected
                    </p>
                    <p className="bs-call-logs-outbound-sections__no-ai-summary-description">
                      This call was connected but no speech was detected.
                    </p>
                  </div>
                )}

                {!aiSummaryPending && aiSummaryText && (
                  <div className="bs-call-logs-outbound-sections__panel bs-call-logs-outbound-sections__panel--filled bs-call-logs-outbound-sections__panel--ai-summary">
                    <div className="bs-call-logs-outbound-sections__panel-header">
                      <span className="call-logs-outbound-panel-header-time">
                        {getFormatDate(
                          callLogsProspectProfileSection?.callStartedAt,
                          userTimeZone,
                        )}
                      </span>
                      <CopyButton
                        text={aiSummaryText}
                        copyTooltipText="Copy"
                        resetDelay={1000}
                        placement={Placement.Bottom}
                        className="bs-call-logs-missed-call-detail__copy-btn"
                      />
                    </div>
                    <div className="bs-call-logs-outbound-sections__summary-body-scroll">
                      <CallLogsAiSummaryMarkdown text={aiSummaryText} />
                    </div>
                  </div>
                )}
              </div>
            </TabPane>
            <TabPane
              tab={
                <span
                  className={classNames('bs-call-logs-outbound-sections__tab', {
                    'bs-call-logs-outbound-sections__tab--active':
                      activeTab === 'recording',
                    'tab-recording-and-transcription':
                      activeTab === 'recording',
                  })}
                >
                  Recording &amp; transcription
                </span>
              }
              key="recording"
            >
              <div className="bs-call-logs-outbound-sections__tab-panel">
                <div className="bs-call-logs-outbound-sections__panel bs-call-logs-outbound-sections__panel--filled">
                  {recordingOrTranscriptionAvailable && (
                    <>
                      <span className="call-logs-outbound-panel-header-time">
                        {getFormatDate(
                          callLogsProspectProfileSection?.callStartedAt,
                          userTimeZone,
                        )}
                      </span>
                      {hasRecordingAsset && (
                        <div className="bs-call-logs-outbound-sections__player-shell">
                          <AudioPlayer
                            showTranscriptDownload={hasRecordingAsset}
                            downloadAttachment="recording"
                            recordingDownloadFileName={`call-recording-${recordingDownloadSafeId}.mp3`}
                            pauseOnScroll={false}
                            defaultDuration={row.recordingDuration}
                            audioSrc={audioSrc}
                            className={`voicemail-greeting-audio-player ${
                              isLoadingAudio ? 'audio-player-loading' : ''
                            }`}
                            showPlaybackSpinner={isLoadingAudio}
                            onPlayWithoutSrc={handlePlayWithoutSrc}
                            onDownloadWithoutSrc={handleDownloadWithoutSrc}
                            recordingDownloadUrlFetchPending={
                              recordingDownloadUrlFetchPending
                            }
                            autoPlayWhenSrcAvailable={shouldAutoPlay}
                            onAutoPlayCompleted={handleAutoPlayCompleted}
                          />
                        </div>
                      )}
                      <div className="bs-call-logs-outbound-sections__transcript-wrap">
                        <div className="bs-call-logs-outbound-sections__transcript-header">
                          {/* <span className="outbound-transcription-header">
                          Transcription
                        </span> */}
                          <Divider />
                          {!isTranscriptionInProgress &&
                            text !== 'No speech detected' && (
                              <div className="bs-call-logs-missed-download-icon-container">
                                {hasTranscriptionBody && (
                                  <>
                                    <CopyButton
                                      text={text as string}
                                      copyTooltipText="Copy"
                                      resetDelay={1000}
                                      placement={Placement.Bottom}
                                      className="bs-call-logs-missed-call-detail__copy-btn"
                                    />
                                    <OverlayTooltip text="Download">
                                      <button
                                        type="button"
                                        className="bs-call-logs-missed-download-icon"
                                        disabled={
                                          !hasTranscriptionBody ||
                                          transcriptionPending
                                        }
                                        aria-label="Download transcription"
                                        {...accessibleOnClick(
                                          handleDownloadFormattedTranscription,
                                        )}
                                      >
                                        <Download width={16} height={16} />
                                      </button>
                                    </OverlayTooltip>
                                  </>
                                )}
                              </div>
                            )}
                        </div>
                        <div className="regular-2 gray-txt-9 bs-call-logs-outbound-sections__transcript-body">
                          {transcriptionPending && (
                            <span className="regular-2 gray-txt-11">
                              Transcription in progress…
                            </span>
                          )}
                          {!transcriptionPending && hasTranscriptionBody && (
                            <div className="bs-call-logs-transcript-body-container">
                              {formattedTranscription?.map(
                                (item: TranscriptItem, index: number) => (
                                  <div className="bs-call-logs-transcript-item-container">
                                    {text !== 'No speech detected' && (
                                      <span
                                        className={`bs-call-logs-transcript-item-name-initial ${
                                          item?.role === 'AGENT'
                                            ? ' agent'
                                            : ' customer'
                                        }`}
                                      >
                                        {getInitials(item?.name)}
                                      </span>
                                    )}
                                    {item?.content !== 'No speech detected' ? (
                                      <div
                                        key={`transcript-item-${index + 1}`}
                                        className="bs-call-logs-outbound-sections__transcript-item"
                                      >
                                        <span className="bs-call-logs-transcript-item-name">
                                          {item?.name}
                                          <Dot />
                                          {formatTime(item?.startTime)}
                                        </span>

                                        <div className="bs-call-logs-transcript-item-content">
                                          {item?.content}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="no-speech-detected-text-container">
                                        <span className="no-speech-detected-text-header">
                                          No conversation detected
                                        </span>
                                        <span className="no-speech-detected-text-body">
                                          This call was connected but no speech
                                          was detected.
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                          {!transcriptionPending && !hasTranscriptionBody && (
                            <span className="regular-2 gray-txt-11">
                              No transcript yet.
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {!recordingOrTranscriptionAvailable &&
                    !hasTranscriptionBody && (
                      <span className="no-recording-or-transcription-available">
                        No recording or transcript available.
                      </span>
                    )}
                </div>
              </div>
            </TabPane>
          </Tabs>
        ) : (
          <div className="bs-call-listing-outbound-detail-sections">
            {aiSummaryText && (
              <div className="bs-call-listing-ai-summary-container">
                <div className="bs-call-listing-ai-summary-header">
                  <AiGradientStarIcon
                    width={16}
                    height={16}
                    className="bs-call-logs-outbound-sections__tab-icon"
                    activeType={activeTab === 'ai-summary'}
                  />
                  <span className="bs-call-listing-ai-summary-header-text">
                    AI Summary
                  </span>
                </div>
                <div className="bs-call-listing-ai-summary-text">
                  <div className="bs-call-listing-ai-summary-text-scroll">
                    <CallLogsAiSummaryMarkdown text={aiSummaryText} />
                  </div>
                  <CopyButton
                    text={aiSummaryText}
                    copyTooltipText="Copy"
                    resetDelay={1000}
                    placement={Placement.Bottom}
                    className="bs-call-listing-ai-summary-copy-btn"
                  />
                </div>
              </div>
            )}

            {hasTranscriptionBody && (
              <div className="bs-call-listing-recording-transcription-container">
                <div className="bs-call-listing-recording-transcription-header">
                  Recording & Transcription
                </div>
                <div className="bs-call-listing-recording-transcription-text-container">
                  <AudioPlayer
                    showTranscriptDownload={hasRecordingAsset}
                    downloadAttachment="recording"
                    recordingDownloadFileName={`call-recording-${recordingDownloadSafeId}.mp3`}
                    pauseOnScroll={false}
                    defaultDuration={
                      row.recordingDuration ??
                      callLogsProspectProfileSection?.duration
                    }
                    audioSrc={audioSrc}
                    className={`voicemail-greeting-audio-player ${
                      isLoadingAudio ? 'audio-player-loading' : ''
                    }`}
                    showPlaybackSpinner={isLoadingAudio}
                    onPlayWithoutSrc={handlePlayWithoutSrc}
                    onDownloadWithoutSrc={handleDownloadWithoutSrc}
                    recordingDownloadUrlFetchPending={
                      recordingDownloadUrlFetchPending
                    }
                    autoPlayWhenSrcAvailable={shouldAutoPlay}
                    onAutoPlayCompleted={handleAutoPlayCompleted}
                  />
                  <Divider />

                  {!row?.isTranscriptionInProgress && (
                    <div className="bs-call-logs-missed-download-icon-container">
                      {hasTranscriptionBody ? (
                        <CopyButton
                          text={text as string}
                          copyTooltipText="Copy"
                          resetDelay={1000}
                          placement={Placement.Bottom}
                          className="bs-call-logs-missed-call-detail__copy-btn"
                        />
                      ) : null}
                      <OverlayTooltip text="Download">
                        <button
                          type="button"
                          className="bs-call-logs-missed-download-icon"
                          disabled={
                            !hasTranscriptionBody || transcriptionPending
                          }
                          aria-label="Download transcription"
                          {...accessibleOnClick(
                            handleDownloadFormattedTranscription,
                          )}
                        >
                          <Download width={16} height={16} />
                        </button>
                      </OverlayTooltip>
                    </div>
                  )}

                  <div className="bs-call-logs-transcript-body-container">
                    {formattedTranscription?.map(
                      (item: TranscriptItem, index: number) => (
                        <div className="bs-call-logs-transcript-item-container">
                          <span
                            className={`bs-call-logs-transcript-item-name-initial ${
                              item?.role === 'AGENT' ? ' agent' : ' customer'
                            }`}
                          >
                            {getInitials(item?.name)}
                          </span>
                          <div
                            key={`transcript-item-${index + 1}`}
                            className="bs-call-logs-outbound-sections__transcript-item"
                          >
                            <span className="bs-call-logs-transcript-item-name">
                              {item?.name} <Dot /> {formatTime(item?.startTime)}
                            </span>

                            <div className="bs-call-logs-transcript-item-content">
                              {item?.content}
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

CallLogsOutboundDetailSections.displayName = 'CallLogsOutboundDetailSections';

export default CallLogsOutboundDetailSections;
